import { Injectable } from '@angular/core';
import { HttpContext } from '@angular/common/http';
import {
  Category,
  ChannelUpdatePayload,
  ConfigDispatchResult,
  ConnectionTestResult,
  DeviceChannelConfiguration,
  DeviceConfigurationClear,
  DeviceConfigurationsForDeviceChannelResponse,
  DeviceConfigurationUpdate,
  DeviceFiltersModel,
  DeviceModel,
  DeviceResponseModel,
  DeviceTypeResponseModel,
  InitialConfigDispatch,
  InitialConfigFilesForDevice,
  ManualPhaseCount,
  MeterRole,
  PhaseCount,
  ReadoutStatus,
  SimActionResult,
  SimStatusResult,
  StartReadoutResult,
} from '@core/models';
import {
  DeviceVerifications,
  StartVerificationResult,
  VerificationDetails,
} from '@core/models/verification.model';
import { ApiService } from '@core/services/api.service';
import {
  SKIP_GLOBAL_ERROR_SNACKBAR,
  SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS,
} from '@core/interceptors/http-error.interceptor';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class DeviceService {
  constructor(private readonly apiService: ApiService) {}

  getListDevicesFilter(filter: DeviceFiltersModel): Observable<DeviceResponseModel> {
    return this.apiService.get<DeviceResponseModel>('/devices', filter as any);
  }

  getDeviceDetail(deviceId: string): Observable<DeviceModel> {
    return this.apiService.get<DeviceModel>(`/devices/${deviceId}`);
  }

  /** For a page that reads a device on the side and carries on without it: every failure is the
   * caller's to handle, so none reaches the global snackbar. */
  getDeviceDetailQuietly(deviceId: string): Observable<DeviceModel> {
    return this.apiService.getRaw<DeviceModel>(
      `/devices/${deviceId}`,
      {},
      new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR, true),
    );
  }

  /** Owner-only: a 403 means the user reaches this device through a partnership. The settings tab
   * explains that itself, so the global snackbar stays out of it. */
  getPhaseCount(
    deviceId: string,
  ): Observable<{ phaseCount: PhaseCount | null; phaseCountFixed?: boolean }> {
    return this.apiService.getRaw<{ phaseCount: PhaseCount | null; phaseCountFixed?: boolean }>(
      `/devices/${deviceId}/phase-count`,
      {},
      new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR_FOR_STATUS, [403]),
    );
  }

  updatePhaseCount(deviceId: string, phaseCount: ManualPhaseCount): Observable<any> {
    // The settings tab renders its own feedback for this call - a 400 `detail` verbatim, a
    // generic message otherwise - so the global snackbar would only repeat it.
    return this.apiService.patch(
      `/devices/${deviceId}/phase-count`,
      { phaseCount },
      new HttpContext().set(SKIP_GLOBAL_ERROR_SNACKBAR, true),
    );
  }

  updateMeterRole(deviceId: string, meterRole: MeterRole): Observable<any> {
    return this.apiService.patch(`/devices/${deviceId}/meter-role`, { meter_role: meterRole });
  }

  getDeviceTypes(): Observable<DeviceTypeResponseModel> {
    const payload = {
      limit: 500,
      offset: 0,
    };
    return this.apiService.get('/devices/device-types', payload as any);
  }

  getCategories(): Observable<Category[]> {
    return this.apiService.get<Category[]>('/devices/category');
  }

  /**
   * Links an existing device to a location (installs it). `installerOid` is the acting user's
   * oid; `presetCode`/`installationOid` are optional and only used when the caller has them. A
   * refusal reaches the caller as the HttpErrorResponse, so it can explain it by status and reason.
   */
  provisionDevice(
    deviceId: string,
    locationId: string | number,
    installerOid: string,
    presetCode?: string,
    installationOid?: string,
    context?: HttpContext,
  ): Observable<any> {
    return this.apiService.postRaw(
      `/devices/${deviceId}/provision/${locationId}`,
      {
        installer_oid: installerOid,
        ...(presetCode ? { preset_code: presetCode } : {}),
        ...(installationOid ? { installation_oid: installationOid } : {}),
      },
      { context },
    );
  }

  getDevicesByLocationId(locationId: string): Observable<any> {
    const payload = {
      locationId,
      limit: 5,
      offset: 0,
    };
    return this.apiService.get('/devices', payload as any);
  }

  getChannelConfiguration(deviceId: string): Observable<DeviceChannelConfiguration> {
    return this.apiService.get<DeviceChannelConfiguration>(`/devices/channel-configuration`, {
      deviceId,
    } as any);
  }

  getChannelConfigurations(
    deviceIds: number[],
  ): Observable<{ deviceId: number; configuration: DeviceChannelConfiguration }[]> {
    return this.apiService.post<{ deviceId: number; configuration: DeviceChannelConfiguration }[]>(
      `/devices/channel-configurations`,
      deviceIds,
    );
  }

  getDeviceConfigurationsForDeviceChannel(
    deviceId: string,
    categoryId: number,
  ): Observable<DeviceConfigurationsForDeviceChannelResponse> {
    const payload = {
      deviceId,
      categoryId,
    };
    return this.apiService.get<DeviceConfigurationsForDeviceChannelResponse>(
      `/devices/device-configuration-for-device`,
      payload as any,
    );
  }

  getDeviceConfigurationsForChannelNumber(
    deviceId: string,
    channelNumber: number,
  ): Observable<DeviceConfigurationsForDeviceChannelResponse> {
    const payload = {
      deviceId,
      channelNumber,
    };
    return this.apiService.get<DeviceConfigurationsForDeviceChannelResponse>(
      `/devices/device-configuration-for-device`,
      payload as any,
    );
  }

  sendDeviceConfigurationFileToDevice(
    deviceId: string,
    deviceConfigurationUpdate: DeviceConfigurationUpdate,
  ): Observable<any> {
    return this.apiService.post(`/devices/send-device-configuration`, {
      deviceId,
      ...deviceConfigurationUpdate,
    });
  }

  sendDeviceConfigurationDispatch(
    deviceConfigDispatchId: string,
  ): Observable<ConfigDispatchResult> {
    return this.apiService.get<ConfigDispatchResult>(
      `/devices/send-device-configuration/${deviceConfigDispatchId}`,
    );
  }

  clearDeviceConfigurationFileFromDevice(
    deviceId: string,
    deviceConfigurationClear: DeviceConfigurationClear,
  ): Observable<any> {
    return this.apiService.post(`/devices/clear-device-configuration`, {
      deviceId,
      ...deviceConfigurationClear,
    });
  }

  updateChannelSetting(deviceId: string, payload: ChannelUpdatePayload): Observable<any> {
    return this.apiService.put(`/devices/channel-configuration?deviceId=${deviceId}`, payload);
  }

  executeConnectionTest(deviceId: string): Observable<ConnectionTestResult> {
    return this.apiService.post('/devices/connection-test', { deviceId });
  }

  getVerificationAttempts(
    deviceId: number,
    limit?: number,
    offset?: number,
  ): Observable<DeviceVerifications> {
    const payload = {
      deviceId,
      limit: limit ?? 10,
      offset: offset ?? 0,
    };
    return this.apiService.get<DeviceVerifications>(
      '/devices/verification?deviceId',
      payload as any,
    );
  }

  getVerificationDetails(verificationId: string): Observable<VerificationDetails> {
    return this.apiService.get<VerificationDetails>(`/devices/verification/${verificationId}`);
  }

  performVerification(deviceId: string): Observable<StartVerificationResult> {
    return this.apiService.post<StartVerificationResult>(`/devices/start-verification/${deviceId}`);
  }

  performReadout(deviceId: string): Observable<StartReadoutResult> {
    return this.apiService.post<StartReadoutResult>(`/devices/trigger-pol/${deviceId}`);
  }

  getReadoutStatus(polId: string): Observable<ReadoutStatus> {
    return this.apiService.get<ReadoutStatus>(`/devices/pol/${polId}`);
  }

  getInitialConfigFilesForDevice(deviceId: string): Observable<InitialConfigFilesForDevice> {
    return this.apiService.get<InitialConfigFilesForDevice>(
      `/devices/initial-configuration-for-device/${deviceId}`,
    );
  }

  sendInitialConfigToDevice(deviceId: string, fileId: number): Observable<InitialConfigDispatch> {
    return this.apiService.post<InitialConfigDispatch>(
      `/devices/send-initial-configuration?deviceId=${deviceId}&fileId=${fileId}`,
    );
  }

  getInitialConfigDispatchStatus(
    initialConfigDispatchId: string,
  ): Observable<ConfigDispatchResult> {
    return this.apiService.get<ConfigDispatchResult>(
      `/devices/send-initial-configuration/${initialConfigDispatchId}`,
    );
  }

  /** Both Xemex and the owning partner's users may call this (mirrors the get_device auth
   * fallback pattern). */
  refreshSim(deviceId: string): Observable<SimStatusResult> {
    return this.apiService.post<SimStatusResult>(`/devices/refresh-sim/${deviceId}`);
  }

  /** Xemex-only. `iccid` is the caller's confirmation echo of the device's current SIM — the
   * backend 409s on mismatch, 404s if the device has no SIM. */
  topUpSim(deviceId: string, iccid: string): Observable<SimActionResult> {
    return this.apiService.post<SimActionResult>(`/devices/top-up-sim/${deviceId}`, { iccid });
  }

  /** Xemex-only, same confirmation contract as {@link topUpSim} — the one SIM action that still
   * works even when the SIM has already expired. */
  extendSim(deviceId: string, iccid: string): Observable<SimActionResult> {
    return this.apiService.post<SimActionResult>(`/devices/extend-sim/${deviceId}`, { iccid });
  }
}
